import ImageIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ImageIconPath";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { useTranslation } from "@coworker/locales";
import styled from "styled-components";
import { useMemo, useState } from "react";
import { useWriteSignedUrls } from "../hooks/useWriteSignedUrls";
import { useImageUploader } from "@coworker/apprestructured/src/uploader/useImageUploader";
import { useAddMediaImages } from "../hooks/useAddMediaImages";
import { AddImagesModal } from "@coworker/apprestructured/src/task-management/components/AddImagesModal/AddImagesModal";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import SomethingWentWrongPrompt from "@coworker/apprestructured/src/task-management/components/SomethingWentWrongPrompt/SomethingWentWrongPrompt";

const FixedHeightContainer = styled.div`
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

interface AddImagesToNoImagesMediaProps {
  mediaId: string;
}

export const AddImagesToNoImagesMedia = ({
  mediaId,
}: AddImagesToNoImagesMediaProps) => {
  const { t } = useTranslation();

  const [addImagesModalOpen, setAddImagesModalOpen] = useState(false);
  const [images, setImages] = useState([] as NewOrExistingFile[]);
  const [submitting, setSubmitting] = useState(false);

  const useWriteSignedUrlsFunc = useMemo(() => useWriteSignedUrls, []);
  const imageSize = useMemo(() => ({ maxWidthOrHeight: 1024 }), []);

  const {
    upload: uploadImages,
    createUploads: imagesCreateUploads,
    error: imageUploaderError,
  } = useImageUploader(images, imageSize, useWriteSignedUrlsFunc);

  const { addMediaImages, error: addMediaImagesError } = useAddMediaImages({
    images,
    imagesCreateUploads,
  });

  const addImagesHandler = async () => {
    setSubmitting(true);
    try {
      await uploadImages();
      await addMediaImages(mediaId);
    } finally {
      setSubmitting(false);
    }
  };

  const showSomethingWentWrongPrompt: boolean =
    !!imageUploaderError || !!addMediaImagesError;

  const handleRefreshClick = () => {
    setTimeout(() => {
      window.location.reload();
    }, 0);
  };

  return (
    <FixedHeightContainer>
      <FixaText textStyle="Heading.L">{t("noImagesAddedYetString")}</FixaText>
      <FixaButton
        ssrIcon={ImageIconPath}
        onClick={() => setAddImagesModalOpen(true)}
        iconPosition="leading"
        text={t("addImagesString")}
        size="medium"
        disabled={submitting}
        type="primary"
      />
      <AddImagesModal
        isOpen={addImagesModalOpen}
        images={images}
        onImagesChange={setImages}
        onClose={() => setAddImagesModalOpen(false)}
        onConfirmClick={addImagesHandler}
      />
      {showSomethingWentWrongPrompt && (
        <SomethingWentWrongPrompt onRefreshClick={handleRefreshClick} />
      )}
    </FixedHeightContainer>
  );
};
